import { useEffect, useState } from "react";
import { api, formatFCFA, mediaSrc } from "@/lib/api";
import DateRangePicker from "@/components/Common/DateRangePicker";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import { Users, Store, Package, Phone } from "lucide-react";

export default function AdminAnalytics() {
  const [range, setRange] = useState({ period: "7d" });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics", { params: range }).then(r => setData(r.data));
  }, [range]);

  if (!data) return <div className="py-8 text-center text-sm text-[#73706B]">Chargement…</div>;

  const kpis = data.kpis || {};

  return (
    <div className="space-y-4">
      <DateRangePicker value={range} onChange={setRange} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi icon={Users} label="Nouveaux utilisateurs" value={kpis.new_users || 0} />
        <Kpi icon={Store} label="Nouvelles boutiques" value={kpis.new_shops || 0} />
        <Kpi icon={Package} label="Nouveaux produits" value={kpis.new_products || 0} />
        <Kpi icon={Phone} label="Contacts" value={kpis.new_contacts || 0} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <ChartCard title="Croissance utilisateurs" series={data.trends?.users} color="#E65100" />
        <ChartCard title="Contacts" series={data.trends?.contacts} color="#0A7D3A" />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <ListCard title="Top catégories (produits)" rows={(data.top_categories || []).map(c => ({ label: c.category, value: `${c.count} prod. · ${c.views} vues` }))} />
        <ListCard title="Top villes (boutiques)" rows={(data.top_cities || []).map(c => ({ label: c.city, value: `${c.shops} boutiques` }))} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <ListCard title="Top marchés" rows={(data.top_markets || []).map(m => ({ label: m.market, value: `${m.shops} boutiques` }))} emptyLabel="Aucun marché renseigné" />
        <ListCard title="Top boutiques (vues)" rows={(data.top_shops || []).map(s => ({ label: s.name + (s.verified ? " ✓" : ""), value: `${s.views} vues · ${s.city || "?"}` }))} />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <ListCard title="Produits les plus vus" rows={(data.top_products || []).map(p => ({ label: p.title, value: `${p.views} vues · ${p.favorites} ♥`, image: p.image }))} />
        <ListCard title="Produits les plus favorisés" rows={(data.top_fav_products || []).map(p => ({ label: p.title, value: `${p.favorites} ♥`, image: p.image }))} />
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="p-3 rounded-xl bg-white border border-[#E8E6E1]">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#73706B]">{label}</div>
        <Icon className="h-4 w-4 text-[#73706B]" />
      </div>
      <div className="mt-1 font-[Manrope] font-extrabold text-xl text-[#1A1918]">{value}</div>
    </div>
  );
}

function ChartCard({ title, series, color }) {
  const rows = series || [];
  return (
    <div className="p-3 rounded-xl bg-white border border-[#E8E6E1]">
      <div className="text-xs uppercase tracking-wider text-[#73706B] font-bold">{title}</div>
      <div className="h-40 mt-2">
        {rows.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-[#73706B]">Aucune donnée</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows}>
              <CartesianGrid stroke="#E8E6E1" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#73706B" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#73706B" }} />
              <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke={color} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function ListCard({ title, rows, emptyLabel }) {
  return (
    <div className="p-3 rounded-xl bg-white border border-[#E8E6E1]">
      <div className="text-xs uppercase tracking-wider text-[#73706B] font-bold mb-2">{title}</div>
      <div className="space-y-1.5">
        {rows.length === 0 ? (
          <div className="text-xs text-[#73706B] py-2">{emptyLabel || "Aucune donnée"}</div>
        ) : rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            {r.image && <div className="w-8 h-8 rounded bg-[#F3F1EC] overflow-hidden shrink-0"><img src={mediaSrc(r.image)} alt="" className="w-full h-full object-cover" /></div>}
            <div className="flex-1 min-w-0 truncate">{r.label || "—"}</div>
            <div className="text-xs text-[#73706B] shrink-0">{r.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
